import React, { useRef, useState } from "react";
import {
  TableRow,
  TableCell,
  IconButton,
  Typography,
  Box,
  Chip,
  Collapse,
  Table,
  TableHead,
  TableBody,
  CircularProgress,
  Stack,
  Button,
} from "@mui/material";
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  MoreVert as MoreVertIcon,
  Description as DescriptionIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Description,
  // Download,
} from "@mui/icons-material";
import { getStatusColor, formatFileName, formatFileType } from "./utils";
import { resolveLeadStatusName, resolveLastStatusLog } from "./leadFormHelpers";
import Can from "../permissions/Can";
import ActionMenu from "./ActionMenu";
import { useUploadAttachmentMutation } from "../../store/api/attachmentsApi";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { toast } from "react-toastify";
import RequestAttachmentModal from "./RequestAttachmentModal";
import AttachmentModal from "./AttachmentModal";
import AttachmentPreviewModal from "./AttachmentPreviewModal";

const LeadRow = ({
  row,
  onOpenMenu,
  processingId,
  onDownloadFile,
  onViewLead,
  onRefreshLeads,
}) => {
  const [open, setOpen] = useState(false);
  const [downloadingIndex, setDownloadingIndex] = useState(null);
  const statusName = resolveLeadStatusName(row);
  const lastStatusLog = resolveLastStatusLog(row);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [uploadingOrderId, setUploadingOrderId] = useState(null);
  const [requestAttachmentOpen, setRequestAttachmentOpen] = useState(false);
  const [requestOrderId, setRequestOrderId] = useState(null);

  const [attachmentsModalOpen, setAttachmentsModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);

  const handleDownload = async (file, index) => {
    setDownloadingIndex(index);
    await onDownloadFile(file, index);
    setDownloadingIndex(null);
  };

  const [uploadAttachment] = useUploadAttachmentMutation();
  const fileInputRef = useRef(null);

  const handleSelectFile = (orderId) => {
    setSelectedOrderId(orderId);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    console.log("event", event.target.files?.[0]);
    const file = event.target.files?.[0];

    if (!file) return;

    const formData = new FormData();

    formData.append("model_type", "Order");
    formData.append("model_id", selectedOrderId);
    formData.append("file", file);

    try {
      setUploadingOrderId(selectedOrderId);
      await uploadAttachment(formData).unwrap();

      await onRefreshLeads?.();

      toast.success("File uploaded successfully");
    } catch (error) {
      console.error("File upload failed:", error);

      toast.error(error?.data?.message || "Failed to upload file");
    }
    setUploadingOrderId(selectedOrderId);
    event.target.value = "";
    setSelectedOrderId(null);
  };

  const handleRequestAttachment = (orderId) => {
    setRequestOrderId(orderId);
    setRequestAttachmentOpen(true);
  };

  const handleOpenAttachments = (order) => {
    setSelectedOrder(order);
    setAttachmentsModalOpen(true);
  };

  const handleCloseAttachments = () => {
    setAttachmentsModalOpen(false);
    setSelectedOrder(null);
  };

  const handlePreview = (file) => {
    setPreviewFile(file);
  };

  const handleClosePreview = () => {
    setPreviewFile(null);
  };

  return (
    <React.Fragment>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          <Typography
            variant="subtitle2"
            fontWeight={500}
            onClick={() => onViewLead?.(row.id)}
            sx={{
              cursor: onViewLead ? "pointer" : "default",
              color: onViewLead ? "primary.main" : "inherit",
              "&:hover": onViewLead
                ? { textDecoration: "underline" }
                : undefined,
            }}
          >
            {row.name}
          </Typography>
        </TableCell>
        <TableCell>{row.email}</TableCell>
        <TableCell>{row.phone}</TableCell>
        <TableCell>{row?.brand?.name ?? "_"}</TableCell>
        <TableCell>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Chip
              label={statusName || "—"}
              color={getStatusColor(statusName)}
              size="small"
            />
            {statusName === "Meeting" && row.meeting_info && (
              <Chip
                label={`Meeting @ ${new Date(row.meeting_info.start_datetime).toLocaleString()}`}
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
          </Box>
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {row?.assignees?.length > 0 ? (
              row.assignees.map((assignee) => (
                <Chip
                  key={assignee.id}
                  label={assignee.name || assignee.username || "Unnamed"}
                  variant="outlined"
                  size="small"
                  // optional: color="primary" or color="default"
                />
              ))
            ) : (
              <Chip label="Unassigned" variant="outlined" size="small" />
            )}
          </Stack>
        </TableCell>
        <TableCell>{new Date(row.created_at).toLocaleDateString()}</TableCell>
        <TableCell sx={{ textAlign: "center" }}>
          <IconButton
            onClick={(e) => onOpenMenu(e, row)}
            disabled={processingId === row.id}
          >
            {processingId === row.id ? (
              <CircularProgress size={20} />
            ) : (
              <MoreVertIcon />
            )}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                margin: 1,
                p: 2,
                backgroundColor: "grey.50",
                borderRadius: 1,
              }}
            >
              {/* Status Information */}
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "primary.main" }}
              >
                Status Information
              </Typography>
              <Table size="small" aria-label="status-info" sx={{ mb: 3 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created By</TableCell>
                    {/* <TableCell sx={{ fontWeight: 600 }}>Assignee</TableCell> */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Chip
                        label={statusName || "—"}
                        color={getStatusColor(statusName)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {lastStatusLog?.description ||
                        row.status?.description ||
                        "No description available"}
                    </TableCell>
                    <TableCell>
                      {new Date(row.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>{row.author?.name || "System"}</TableCell>
                    {/* <TableCell>
                      {row?.last_status_log?.assignee?.name || 'Unassigned'}
                    </TableCell> */}
                  </TableRow>
                </TableBody>
              </Table>

              {/* Order Information */}
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "primary.main" }}
              >
                Order Information
              </Typography>

              <Table size="small" aria-label="order-info" sx={{ mb: 3 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Order Number</TableCell>

                    <TableCell sx={{ fontWeight: 600 }}>Services</TableCell>

                    <TableCell sx={{ fontWeight: 600 }}>Attachments</TableCell>

                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>

                    <TableCell sx={{ fontWeight: 600 }}>Sale Type</TableCell>

                    <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>

                    <TableCell sx={{ fontWeight: 600 }}>Address</TableCell>

                    <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {row?.orders?.length > 0 ? (
                    row.orders.map((order) => {
                      // const uploadedAttachments =
                      //   order.attachments?.filter(
                      //     (file) => file.status !== "Required",
                      //   ) || [];

                      // const requiredAttachments =
                      //   order.attachments?.filter(
                      //     (file) => file.status === "Required",
                      //   ) || [];
                      return (
                        <TableRow key={order.id} hover>
                          <TableCell>{order.order_number || "—"}</TableCell>

                          {/* SERVICES */}
                          <TableCell>
                            {Array.isArray(order.services) &&
                            order.services.length > 0 ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.5,
                                }}
                              >
                                {order.services.map((item) => (
                                  <Chip
                                    key={item.id}
                                    label={
                                      item?.service?.name || "Unknown Service"
                                    }
                                    size="small"
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          {/* Documents */}
                          <TableCell>
                            {Array.isArray(order.attachments) &&
                            order.attachments.length > 0 ? (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleOpenAttachments(order)}
                              >
                                <Description
                                  fontSize="small"
                                  sx={{ mr: 0.5 }}
                                />
                                {order.attachments.length} Files
                              </Button>
                            ) : (
                              "—"
                            )}
                          </TableCell>

                          <TableCell>
                            <Chip
                              label={order.status || "—"}
                              size="small"
                              color={
                                order.status === "paid"
                                  ? "success"
                                  : order.status === "unpaid"
                                    ? "warning"
                                    : "default"
                              }
                            />
                          </TableCell>

                          <TableCell>{order.sale_type || "—"}</TableCell>

                          <TableCell>
                            ${Number(order.amount || 0).toLocaleString()}
                          </TableCell>

                          <TableCell>{order.address || "—"}</TableCell>

                          <TableCell>{order.created_at || "—"}</TableCell>
                          <TableCell>
                            <ActionMenu
                              items={[
                                {
                                  label: "Request Attachment",
                                  icon: <DescriptionIcon fontSize="small" />,
                                  onClick: () => {
                                    handleRequestAttachment(order.id);
                                  },
                                },

                                {
                                  label:
                                    uploadingOrderId === order.id
                                      ? "Uploading..."
                                      : "Upload File",

                                  icon:
                                    uploadingOrderId === order.id ? (
                                      <CircularProgress size={18} />
                                    ) : (
                                      <UploadFileIcon fontSize="small" />
                                    ),

                                  onClick: () => handleSelectFile(order.id),

                                  disabled: uploadingOrderId === order.id,
                                },
                              ]}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        No orders found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {/* Meeting Information */}
              {statusName === "Meeting" &&
                row?.meetings &&
                Array.isArray(row.meetings) &&
                row.meetings.length > 0 && (
                  <>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ color: "primary.main", mt: 2 }}
                    >
                      Meeting Information
                    </Typography>

                    <Table
                      size="small"
                      aria-label="meeting-info"
                      sx={{ mb: 3 }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Time Zone
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Start Date & Time
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            End Date & Time
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Description
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {row.meetings.map((meeting, index) => (
                          <TableRow key={meeting.id || index}>
                            <TableCell>{meeting.time_zone || "UTC"}</TableCell>
                            <TableCell>
                              {meeting.start_date_time
                                ? new Date(
                                    meeting.start_date_time,
                                  ).toLocaleString()
                                : "N/A"}
                            </TableCell>
                            <TableCell>
                              {meeting.end_date_time
                                ? new Date(
                                    meeting.end_date_time,
                                  ).toLocaleString()
                                : "N/A"}
                            </TableCell>
                            <TableCell>
                              {meeting.description || "No meeting description"}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={statusName || "Meeting"}
                                color={getStatusColor(statusName)}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )}

              {/* Attachments */}
              {row?.attachments && row?.attachments?.length > 0 && (
                <>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "primary.main", mt: 2 }}
                  >
                    Attachments
                  </Typography>
                  <Table size="small" aria-label="attachments">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Uploaded At
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {row.attachments.slice(0, 7).map((file, idx) => {
                        const fileName = formatFileName(file, idx);
                        const filePath = file?.path;
                        const fileType = formatFileType(file, filePath);
                        const isDownloading = downloadingIndex === idx;

                        return (
                          <TableRow key={idx} hover>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <DescriptionIcon
                                  fontSize="small"
                                  color="action"
                                />
                                <Typography variant="body2" noWrap>
                                  {fileName}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip label={fileType} size="small" />
                            </TableCell>
                            <TableCell>
                              {file?.created_at
                                ? new Date(file.created_at).toLocaleDateString()
                                : "Unknown"}
                            </TableCell>
                            <TableCell align="center">
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 0.5,
                                  justifyContent: "center",
                                }}
                              >
                                {filePath && (
                                  <Can permission="attachment-view">
                                    <IconButton
                                      size="small"
                                      component="a"
                                      href={filePath}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      title="View"
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Can>
                                )}
                                {filePath && (
                                  <Can permission="attachment-view">
                                    {isDownloading ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handleDownload(file, idx)
                                        }
                                        title="Download"
                                      >
                                        <DownloadIcon fontSize="small" />
                                      </IconButton>
                                    )}
                                  </Can>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {row.attachments.length > 7 && (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography variant="body2" color="primary">
                              + {row.attachments.length - 3} more attachments
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </>
              )}

              {(!row.attachments || row.attachments.length === 0) && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  No attachments available
                </Typography>
              )}

              {/* Issue Information */}
              {row.issue && (
                <>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "primary.main", mt: 2 }}
                  >
                    Lead Issue Information
                  </Typography>
                  <Table size="small" aria-label="meeting-info" sx={{ mb: 3 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Description
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          End Date & Time
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>{row?.issue?.title || "N/A"}</TableCell>
                        <TableCell>{row?.issue?.description}</TableCell>
                        <TableCell>
                          {new Date(row.issue?.created_at).toLocaleString()}
                        </TableCell>
                        {/*
                        <TableCell>
                          {row.last_status_log?.meeting?.description || 'No meeting description'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row?.last_status_log?.status?.name}
                            color={getStatusColor(row?.status?.meeting?.status?.name)}
                            size="small"
                          />
                        </TableCell> */}
                      </TableRow>
                    </TableBody>
                  </Table>
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      <RequestAttachmentModal
        open={requestAttachmentOpen}
        orderId={requestOrderId}
        onClose={() => {
          setRequestAttachmentOpen(false);
          setRequestOrderId(null);
        }}
        onSuccess={onRefreshLeads}
      />
      <AttachmentModal
        open={attachmentsModalOpen}
        order={selectedOrder}
        onClose={handleCloseAttachments}
        onPreview={handlePreview}
      />
      <AttachmentPreviewModal
        open={Boolean(previewFile)}
        file={previewFile}
        onClose={handleClosePreview}
      />

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        hidden
        onChange={handleFileChange}
      />
    </React.Fragment>
  );
};

export default LeadRow;
